import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { getMyChargerHistory } from '../services/charger-history-service';
import { listMyChargers } from '../services/charger-service';
import { isSuccessful } from '../services/api-requests';
import { ChargerAttributes } from '../models/charger-model';

dayjs.extend(utc);

export interface InsightsFilters {
  startDate: string; // ISO YYYY-MM-DD
  endDate: string;   // ISO YYYY-MM-DD
  chargerIds?: string[]; // empty or undefined means all
}

export interface DailyKwhPoint {
  date: string; // YYYY-MM-DD
  totalKwh: number;
}

export interface EarningsPoint {
  date: string; // YYYY-MM-DD
  accumulated: number; // cumulative sum
}

export interface HostEarningsPoint {
  date: string; // YYYY-MM-DD
  amount: number; // earnings for that day (not accumulated)
}

export interface ActiveBreakdown {
  activeCount: number;
  inactiveCount: number;
}

export interface HostInsightsData {
  dailyKwh: DailyKwhPoint[];
  accumulatedEarnings: EarningsPoint[];
  hostEarnings: HostEarningsPoint[];
  breakdown: ActiveBreakdown;
  lifetimeSums?: {
    allKwh: number;
    hostEarnings: number;
  };
}

const toIsoDay = (d: Date): string => d.toISOString().split('T')[0];

const buildDateBuckets = (start: dayjs.Dayjs, end: dayjs.Dayjs): string[] => {
  const buckets: string[] = [];
  let curr = start;
  while (curr.isBefore(end) || curr.isSame(end)) {
    buckets.push(curr.format('YYYY-MM-DD'));
    curr = curr.add(1, 'day');
  }
  return buckets;
};

export function useHostInsightsData(filters?: Partial<InsightsFilters>) {
  const defaultStart = dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const defaultEnd = dayjs().format('YYYY-MM-DD');

  const [selectedStartDate, setSelectedStartDate] = useState<string>(filters?.startDate || defaultStart);
  const [selectedEndDate, setSelectedEndDate] = useState<string>(filters?.endDate || defaultEnd);
  const [selectedChargerIds, setSelectedChargerIds] = useState<string[] | undefined>(filters?.chargerIds);
  const [chargers, setChargers] = useState<ChargerAttributes[]>([]);
  const [loading, setLoading] = useState(false);

  const [data, setData] = useState<HostInsightsData>({
    dailyKwh: [],
    accumulatedEarnings: [],
    hostEarnings: [],
    breakdown: { activeCount: 0, inactiveCount: 0 },
    lifetimeSums: { allKwh: 0, hostEarnings: 0 },
  });

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch my chargers
      const chargersResp = await listMyChargers();
      if (!isSuccessful(chargersResp?.status) || !chargersResp?.payload) {
        console.error('Failed to fetch host chargers');
        setLoading(false);
        return;
      }

      const myChargers = chargersResp.payload;
      setChargers(myChargers);

      // Fetch charger histories
      const historyResp = await getMyChargerHistory();
      if (!isSuccessful(historyResp?.status) || !historyResp?.payload) {
        console.error('Failed to fetch host charger history');
        setLoading(false);
        return;
      }

      const allHistory = historyResp.payload;

      // Filter by selected chargers
      const chargerIdSet = selectedChargerIds && selectedChargerIds.length > 0 
        ? new Set(selectedChargerIds) 
        : null;
      
      const filteredHistory = chargerIdSet 
        ? allHistory.filter(h => chargerIdSet.has(h.chargerId ?? ''))
        : allHistory;

      // Build date buckets in UTC to match server timestamps
      // Special handling: if current UTC date is ahead of local date,
      // extend range to capture transactions that occurred "today" locally but "tomorrow" in UTC
      const currentLocalDate = dayjs().format('YYYY-MM-DD');
      const currentUtcDate = dayjs.utc().format('YYYY-MM-DD');
      
      let endDateForBuckets = selectedEndDate;
      // If the selected end date is "today" locally, but UTC has rolled to tomorrow,
      // extend the end date by one day to capture all of today's local transactions
      if (selectedEndDate === currentLocalDate && currentUtcDate > currentLocalDate) {
        endDateForBuckets = dayjs(selectedEndDate).add(1, 'day').format('YYYY-MM-DD');
      }
      
      const dayKeys = buildDateBuckets(
        dayjs.utc(selectedStartDate),
        dayjs.utc(endDateForBuckets)
      );

      // Aggregate daily kWh (sum across chargers per day)
      const kwhPerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));
      const revenuePerDay = new Map<string, number>(dayKeys.map(d => [d, 0]));

      // For lifetime sums (all time, not just selected date range)
      let lifetimeKwh = 0;
      let lifetimeRevenue = 0;

      filteredHistory.forEach(h => {
        const dateRaw = h?.charging_end_time;
        if (!dateRaw) return;
        const d = new Date(dateRaw);
        if (isNaN(d.getTime())) return;
        // Convert to UTC date for comparison with UTC buckets
        const key = dayjs(d).utc().format('YYYY-MM-DD');

        const kwh = Number(h?.total_kwh) || 0;
        const revenue = Number(h?.total_charge_price) || 0;

        // Lifetime sums (regardless of date range)
        lifetimeKwh += kwh;
        lifetimeRevenue += revenue;

        // Only add to daily aggregates if within selected range
        if (kwhPerDay.has(key)) {
          kwhPerDay.set(key, (kwhPerDay.get(key) || 0) + kwh);
          revenuePerDay.set(key, (revenuePerDay.get(key) || 0) + revenue);
        }
      });

      // Convert to arrays
      const dailyKwh = dayKeys.map(date => ({
        date,
        totalKwh: kwhPerDay.get(date) || 0,
      }));

      const hostEarnings = dayKeys.map(date => ({
        date,
        amount: revenuePerDay.get(date) || 0,
      }));

      // Calculate accumulated earnings
      let accumulated = 0;
      const accumulatedEarnings = dayKeys.map(date => {
        accumulated += revenuePerDay.get(date) || 0;
        return {
          date,
          accumulated,
        };
      });

      // Active/Inactive breakdown
      const activeCount = myChargers.filter(
        c => c.isOccupied === 1 || c.is_enabled === true
      ).length;
      const inactiveCount = myChargers.length - activeCount;

      setData({
        dailyKwh,
        accumulatedEarnings,
        hostEarnings,
        breakdown: { activeCount, inactiveCount },
        lifetimeSums: {
          allKwh: lifetimeKwh,
          hostEarnings: lifetimeRevenue,
        },
      });
    } catch (error) {
      console.error('Error fetching host insights data:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedStartDate, selectedEndDate, selectedChargerIds]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return {
    data,
    chargers,
    loading,
    selectedStartDate,
    selectedEndDate,
    selectedChargerIds,
    setSelectedStartDate,
    setSelectedEndDate,
    setSelectedChargerIds,
    refetch,
  };
}

